import buffer from "buffer"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { NIL as uuidNull } from "uuid"
import { EncodingType, IdTableVariant, SearchOption } from "../../@types/enumsGlobal"
import { KizBoxEntity } from "../../Business/Dictionaries/Kiz/KizBoxEntity"
import { KizParsed } from "../../Business/Dictionaries/Kiz/KizParsed"
import { ILotByIdDTO, ILotsByIdDTO } from "../../libs/core-api-requests/accounting/lotRequest"
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common"
import { GoodsDataProvider } from "../../Services/DataProviders/GoodsDataProvider"
import { KizDataProvider } from "../../Services/DataProviders/KizDataProvider"
import { LotDataProvider } from "../../Services/DataProviders/LotDataProvider"
import { cyrillicKeyboardTranslation } from "../../system/functions/keyboardLanguageTranslation"
import useGridFilter, { CreateSelectorGridFilter } from "../../system/hooks/useGridFilter"
import { useAppContext } from "../../system/providers/appContextProvider"
import { DictionaryInput } from "./dictionaryInput"
import { useDocumentTabSettings } from "../../system/hooks/useDocumentTabSettings"
import { StoreNameEnum } from "../../libs/ui-settings-manager/interfaces"
import { KizEntity } from "../../Business/Dictionaries/Kiz/KizEntity"
import LotsMultipleSelectorModal from "../../Business/Accounting/Lots/LotsMultipleSelectorModal"
import { ILotsViewItem } from "../../Business/Accounting/Lots/LotsSelector"
import LotsSimpleSelectorModal from "../../Business/Accounting/Lots/LotsSimpleSelectorModal"

const GridSelectorInput: FC<IGridSelectorInputProps> = (props) => {
    const appContext = useAppContext()
    const lotDP = new LotDataProvider(appContext.coreApiService)
    const goodsDP = new GoodsDataProvider(appContext.coreApiService)
    const kizDP = new KizDataProvider(appContext.coreApiService)

    const [visible, setVisible] = useState<boolean>(false)
    const [searchQuery, setSearchQuery] = useState<string>(props.value ?? "")
    const [lastSearchQueries, setLastSearchQueries] = useState<string[]>([])

    const showLastSearchQuery = props.lastSearchQuerySettings?.showLastSearchQuery;
    const documentKey = props.lastSearchQuerySettings?.documentKey
    const { settings, updateSettings, getStoreUISettings } = useDocumentTabSettings(documentKey);
    const autoFocusApplied = useRef<boolean>(false);
    const [lotsViewItem, setLotsViewItem] = useState<ILotsViewItem>({ item: null, show: false, multiple: true });

    const timeoutRef = useRef<NodeJS.Timeout>();
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.gridFilter ?? CreateSelectorGridFilter)
    const [isEnterPressed, setIsEnterPressed] = useState<boolean>(false)

    const inputRef = useRef<HTMLInputElement>(null);

    const safeTrim = (value: any): string => {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (typeof value === 'number') {
            return value.toString().trim();
        }
        return '';
    };

    useEffect(() => {
        if(props.lastSearchQuerySettings?.fieldName) {
            const value = getStoreUISettings(props.lastSearchQuerySettings.fieldName) || []
            setLastSearchQueries(value)
        }
    }, [props.lastSearchQuerySettings?.fieldName])

    // Функция для сохранения запроса в массив
    const saveSearchQuery = useCallback((query: string) => {
        if (!props.lastSearchQuerySettings?.fieldName || !query || safeTrim(query).length === 0) return;
        if (!KizEntity.isKiz(query, EncodingType.base64)) return
        if (KizBoxEntity.isKizBox(query)) return
        if (KizBoxEntity.isKizBoxGismt(query)) return

        const trimmedQuery = safeTrim(query);
        
        setLastSearchQueries(prev => {
            // Создаем новый массив, исключая текущий запрос если он уже есть
            const filteredPrev = prev.filter(q => q !== trimmedQuery);
            // Добавляем новый запрос в начало и ограничиваем до 3 элементов
            const newQueries = [trimmedQuery, ...filteredPrev].slice(0, 3);
            
            const currentStore = settings.documentStore || [];
            const existingItemIndex = currentStore.findIndex(
                item => item.name === StoreNameEnum.lastSearchQuery && item.fieldName === props.lastSearchQuerySettings?.fieldName
            );

            let updatedStore;
            if (existingItemIndex >= 0) {
                updatedStore = currentStore.map((item, index) => 
                    index === existingItemIndex 
                        ? { ...item, value: newQueries }
                        : item
                );
            } else {
                updatedStore = [
                    ...currentStore,
                    {
                        name: StoreNameEnum.lastSearchQuery,
                        fieldName: props.lastSearchQuerySettings?.fieldName,
                        value: newQueries
                    }
                ];
            }

            updateSettings({
                documentStore: updatedStore
            });

            return newQueries;
        });
    }, [props.lastSearchQuerySettings?.fieldName, settings, updateSettings]);

    useEffect(() => {
        if (props.autoFocus && lastSearchQueries.length > 0 && !autoFocusApplied.current) {
            const firstQuery = lastSearchQueries[0];
            setSearchQuery(firstQuery);
            props.onChange?.(firstQuery);
            autoFocusApplied.current = true;
        }
    }, [props.autoFocus, lastSearchQueries]);
    
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    
    useEffect(() => {
        if (props.gridFilter) {
            dispatchGridFilter({
                ...gridFilter,
                type: "newGridFilters",
                payload: props.gridFilter,
            })
        }
    }, [props.gridFilter])

    useEffect(() => {
        if (visible) props.setScanData?.(undefined)
    }, [visible])

    useEffect(() => {
        if (props?.selectedEntity) {
            setSearchQuery(props.numereticInput ? props?.selectedEntity : props.selectedEntity.displayName)
            //addColumnFilter(props.selectedEntity.displayName);
        } else {
            setSearchQuery("")
        }
    }, [props?.selectedEntity])

    useEffect(() => {
        if (props?.searchOption) {
            setSearchQuery("")
            Object.values(SearchOption).forEach((option) => {
                if (props.dispatchGridFilter) {
                    props.dispatchGridFilter?.({
                        type: "deleteColumnFilter",
                        payload: option,
                    })
                } else {
                    dispatchGridFilter({
                        type: "deleteColumnFilter",
                        payload: option,
                    })
                }
            })
        }
    }, [props?.searchOption])

    useEffect(() => {
        if (props?.excludeContractor)
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: "IdGlobal",
                    value: props?.excludeContractor,
                    operator: "NotEq",
                } as IGridColumnFilter,
            })
    }, [props?.excludeContractor])

    const addColumnParamFilter = (payloadValue: string): void => {
        if (payloadValue != null && payloadValue.trim().length != 0) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            goodsName: payloadValue,
                            idGosContractGlobal: props.gosContract,
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            goodsName: payloadValue,
                            idGosContractGlobal: props.gosContract,
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            }
        }
        if (payloadValue.trim().length === 0) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter({
                    type: "deleteSpecificParamFilter",
                    payload: "goodsName",
                });
            } else {
                dispatchGridFilter({
                    type: "deleteSpecificParamFilter",
                    payload: "goodsName",
                });
            }
        }
        if (payloadValue.trim().length == 0 && props.idStoreSupplier) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            }
        }
    }

    const addColumnSimpleFilter = (payloadValue: string, nameColumn: string): void => {
        if (payloadValue) {
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: nameColumn,
                    operator: "Like",
                    value: payloadValue,
                } as IGridColumnFilter,
            })
        }
    }
    const addColumnSimpleFilterNumber = (payloadValue: number, nameColumn: string): void => {
        if (payloadValue) {
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: nameColumn,
                    operator: "Eq",
                    value: payloadValue,
                } as IGridColumnFilter,
            })
        }
    }

    const addColumnAndParamFilter = (payloadValue: string, nameColumn: string): void => {
        const baseParamFilter = props.idStoreSupplier ? { ...gridFilter.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter;
        if (payloadValue != null && payloadValue.trim().length != 0) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [
                            {
                                name: nameColumn,
                                operator: "Like",
                                value: payloadValue,
                            },
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [
                            {
                                name: nameColumn,
                                operator: "Like",
                                value: payloadValue,
                            },
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }
        }
        if (payloadValue.trim().length == 0 && props.idStoreSupplier) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [                            
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }else{
                dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [                           
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }
        }
    }

    const deleteColumnFilter = (): void => {
        if (props.dispatchGridFilter) {
            props.dispatchGridFilter?.({
                type: "paramSimpleFilter",
                payload: {
                    gridColumnFilter: props.gridFilter?.columnFilters,
                    gridParamFilter: props.idStoreSupplier ? { ...props.gridFilter?.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter,
                },
            })
        } else
            dispatchGridFilter?.({
                type: "paramSimpleFilter",
                payload: {
                    gridColumnFilter: props.gridFilter?.columnFilters ?? CreateSelectorGridFilter.columnFilters,
                    gridParamFilter: props.idStoreSupplier ? { ...props.gridFilter?.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter,
                },
            })
    }

    const selectInputText = () => {
        if (!inputRef.current) return;
        
        const input = inputRef.current;
        
        // Если input уже в фокусе
        if (document.activeElement === input) {
            // Сначала сбрасываем фокус
            input.blur();
            
            // Затем с небольшой задержкой возвращаем фокус и выделяем
            setTimeout(() => {
                input.focus();
                setTimeout(() => {
                    input.select();
                }, 10);
            }, 10);
        } else {
            // Если не в фокусе - просто выделяем
            setTimeout(() => {
                input.select();
            }, 10);
        }
    };

    const saveRawBarcode = (lot: ILotByIdDTO, kizParsed: KizParsed) => {

        // Перезаписпь шк только для РН, ПТ, Инвентаризации
        const shouldSaveBarcode = props.idTable && [
            IdTableVariant.InvoiceOut,
            IdTableVariant.Movement,
            IdTableVariant.InventoryVed
        ].includes(props.idTable)

        const hasValidKiz = lot?.idKizGlobalRequested &&
            lot.idKizGlobalRequested.length > 0 &&
            lot.idKizGlobalRequested !== uuidNull

        const encodedBarcodeBase64 = encodeURIComponent(kizParsed.rawBarcodeCorrectedBase64);

        if (shouldSaveBarcode && hasValidKiz) {
            kizDP.saveRawBarcode(lot.idKizGlobalRequested!, encodedBarcodeBase64)
        }
    }

    function searchByGtinSgtin(
        kizParsed: KizParsed,
        options: {
            onSuccess?: (lot: ILotByIdDTO) => void;
            onFailed?: () => void;
            withAutoSelect?: {
                query: string;
                onSelect: (lot: ILotByIdDTO, query: string) => void;
            };
        }
    ) {
        const { onSuccess, onFailed, withAutoSelect } = options;
        const gtinSgtinQuery: string = kizParsed.gtinSgtin === "" ?
            kizParsed.rawBarcode :
            kizParsed.gtinSgtin

        const encodedGtinSgtin = encodeURIComponent(gtinSgtinQuery);

        lotDP.getListLotByGtinSgtin(
            encodedGtinSgtin,
            props.idTable as number,
            props.idStoreGlobal ?? null,
            (lots: ILotsByIdDTO[]) => {

                // Несколько результатов
                if (lots.length > 1) {
                    setLotsViewItem({ item: lots, show: true, multiple: false });
                    saveRawBarcode(lots[0], kizParsed);
                    return;
                }

                const singleLot = lots[0];
                if (!singleLot) return;

                // Автоматический выбор с query
                if (withAutoSelect) {
                    withAutoSelect.onSelect(singleLot, withAutoSelect.query);
                    setSearchQuery(withAutoSelect.query);
                }
                // Обычный обработчик успешного ответа
                else {
                    onSuccess?.(singleLot);
                }
            },
            onFailed
        );
    }

    function searchKizBox(scanQuery: string, rawQuery: string) {

        lotDP.getListByKizBoxBarcode(
            scanQuery,
            props.idStoreGlobal ?? null,
            true,
            (lots: ILotsByIdDTO[]) => {

                // Несколько партий
                if (lots.length > 1) {
                    setLotsViewItem({ item: lots, show: true, multiple: false, queryArg: rawQuery });
                    return;
                }

                const singleLot = lots[0];
                if (!singleLot) return;

                setVisible(false)
                props.onSelect?.(singleLot, rawQuery)
                setSearchQuery("")
            })
    }

    function selectLot(query: string, enter: boolean) {
        enter && setIsEnterPressed(true)
        props.onFocus?.()

        if (enter && query && props.lastSearchQuerySettings?.fieldName && showLastSearchQuery) {
            saveSearchQuery(query);
        }

        let kizParsed = new KizParsed(String(query))

        if (kizParsed.isKiz && props.searchOption !== SearchOption.gtinSgtin && props.searchOption !== SearchOption.gtin) {
            if (query && query.length && query.length > 100 || !props.idTable) return
            
            const onSuccess = (lot: ILotByIdDTO) => {
                saveRawBarcode(lot, kizParsed);

                setSearchQuery("")
                props?.setScanData?.(kizParsed.rawBarcodeCorrected)
                props.onSelect?.(lot, kizParsed.rawBarcodeCorrected)
            }

            searchByGtinSgtin(kizParsed, {
                onSuccess,
                onFailed: selectInputText
            });
            return
        }

        if (props.searchOption === SearchOption.name || props.searchOption === SearchOption.goodsName) {
            if (props.valid) {
                if (props.valid()) {
                    handleOpenModal()
                    addColumnParamFilter(query)                    
                }
            } else {
                handleOpenModal()
            }
            setSearchQuery(query ?? "")
            if (enter || !enter) {
                addColumnParamFilter(query)
            }
        } else if (props.searchOption === undefined) {
            if (enter) {
                handleOpenModal()
                addColumnSimpleFilter(query, "name")
            } else if (!enter) {
                handleOpenModal()
            }
        } else if (props.searchOption === SearchOption.tradeName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnAndParamFilter(query, 'tradeNameRus')                
            }           
        } else if (props.searchOption === SearchOption.substanceName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnAndParamFilter(query, 'substanceNameRus')                
            }           
        } else if (props.searchOption === SearchOption.cureFormName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnSimpleFilter(query, 'cureFormName')                
            }           
        } else if (props.searchOption === "n_fv") {
            if (enter || !enter) {
                handleOpenModal()
                const numericValue = parseFloat(query);
                if (!isNaN(numericValue)) {
                    addColumnSimpleFilterNumber(numericValue, 'nFv')

                }
            }  
        } else if (props.searchOption === SearchOption.dosage) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnSimpleFilter(query, 'dosage')                
            }           
        } else if (props.searchOption === SearchOption.gtinSgtin) {
            if (!query) {
                handleOpenModal()
                return
            }
            const onSuccess = (lot: ILotByIdDTO) => {
                const lotKizDB = lot?.kizDto?.decodedBarcode
                props?.setScanData && props?.setScanData(lotKizDB)

                props.onSelect?.(lot, lotKizDB)
                setSearchQuery("")
            };

            searchByGtinSgtin(kizParsed, {
                onSuccess,
                onFailed: selectInputText
            });

        } else if (props.searchOption === SearchOption.kiz) {
            if (!query) {
                handleOpenModal()
                return
            }

            const onSuccess = (lot: ILotByIdDTO) => {
                saveRawBarcode(lot, kizParsed);

                props.onSelect?.(lot, query)
                setSearchQuery("")
            };

            searchByGtinSgtin(kizParsed, {
                onSuccess,
                onFailed: selectInputText
            });

        } else if (props.searchOption === SearchOption.kizBox) {
            if (!query) {
                handleOpenModal()
                return
            }
            const scanDataBase64: string = buffer.Buffer.from(query, "ascii").toString("base64")
            searchKizBox(scanDataBase64, query);
        } else if (props.searchOption === SearchOption.kizOrKizBox) {
            if (!query) {
                handleOpenModal()
                return
            }
            let valueSearchQuery = query.length === 18 ? "00" + query : query
            const scanDataBase64: string = buffer.Buffer.from(valueSearchQuery, "ascii").toString("base64")
            if (KizBoxEntity.isKizBox(query)) {
                searchKizBox(scanDataBase64, valueSearchQuery)
            } else {
                const onSuccess = (lot: ILotByIdDTO) => {
                    saveRawBarcode(lot, kizParsed);

                    props.onSelect?.(lot, valueSearchQuery)
                    setSearchQuery("")
                }

                searchByGtinSgtin(kizParsed, {
                    onSuccess,
                    onFailed: selectInputText
                });
            }
        } else if (props.searchOption === SearchOption.gtin) {
            if (!query) {
                handleOpenModal()
                return
            }
            let valueSearchQuery = query.length === 18 ? "00" + query : query
            props.onSelect?.(null, valueSearchQuery)
            setSearchQuery("")
        } else if (props.searchOption === SearchOption.barcode) {
            if (!query) {
                handleOpenModal()
                return
            }
            lotDP.findManyByInternalBarcode(query, (e) => {
                if (Array.isArray(e) && e.length > 1) {
                    setLotsViewItem({ item: e, show: true, multiple: true });
                    return;
                }

                props.onSelect?.(e, query)
                setSearchQuery(query ?? "")
            },
            props.idStoreGlobal
        )
        } else if (props.searchOption === SearchOption.goodsCode) {
            if (!query) {
                handleOpenModal()
                return
            }
            if (props.gosContract) {
                goodsDP.getGoodsByCodeWithGosContract(query, props.gosContract, (e) => {
                    if (e) {
                        props.onSelect?.({ displayName: e[0].goodsName.name, idGlobal: e[0].idGlobal } as IEntitySimpleDTO, query)
                        setSearchQuery(query ?? "")
                    } else {
                        addColumnSimpleFilter(query, "code")
                        handleOpenModal()
                        setSearchQuery(query ?? "")
                    }
                })
            } else {
                goodsDP.getGoodsByCode(query, (e) => {
                    if (e) {
                        props.onSelect?.({ displayName: e[0].goodsName.name, idGlobal: e[0].idGlobal } as IEntitySimpleDTO, query)
                        setSearchQuery(query ?? "")
                    } else {
                        addColumnSimpleFilter(query, "code")
                        handleOpenModal()
                        setSearchQuery(query ?? "")
                    }
                })
            }
        } else if (props.searchOption === SearchOption.goodsCodeBylot) {
            if (!query) {
                handleOpenModal()
                return
            }

            addColumnSimpleFilter(query, SearchOption.goodsCode)
            handleOpenModal()
            setSearchQuery(query ?? "")
        } else if (props.searchOption === SearchOption.internalBarcode) {
            if (!query) {
                handleOpenModal()
                return
            }
            lotDP.findManyByInternalBarcode(query, (e) => {
                if (Array.isArray(e) && e.length > 1) {
                    setLotsViewItem({ item: e, show: true, multiple: true });
                    return;
                }

                props.onSelect?.(e, query)
                setSearchQuery(query ?? "")
                if (props.idTable && (props.idTable === IdTableVariant.ActDisassembling || props.idTable === IdTableVariant.Movement)) {
                } else
                    handleOpenModal()
            },
                props.idStoreGlobal
            )
        }
    }

    const onClick = (e?: string): string => {
        let value = e as string;
        let trimValue = (props.searchOption === 'kizOrKizBox' || props.searchOption === 'kizBox') ? cyrillicKeyboardTranslation(value.trim()) : value;
        if (props.searchOption === SearchOption.kizBox && trimValue.length == 18 && trimValue[0] !== undefined) {
            trimValue = '00' + trimValue;
            props.onChange?.(trimValue);
            setSearchQuery(trimValue);
        } else {
            props.onChange?.(trimValue)
            setSearchQuery(trimValue)
        }
        if (trimValue != null && trimValue.length != 0) {
            //addColumnFilter(e as string);
        } else {
            deleteColumnFilter()
        }
        return trimValue
    }

    const handleOpenModal = () => {
        props.onSelectorOpen?.();
        setVisible(true);
    };

    const handleCloseModal = () => {
        props.onSelectorClose?.();
        setVisible(false);
    };

    const handleFocus = () => {
        props.onFocus?.();
        
        if (!searchQuery && lastSearchQueries.length > 0 && showLastSearchQuery) {
            const lastQuery = lastSearchQueries[0];
            setSearchQuery(lastQuery);
            props.onChange?.(lastQuery);
        }
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.select();
                timeoutRef.current = undefined;
            }
        }, 10);
    };

    const handleClearLastSearchQueries = () => {
        if (!props.lastSearchQuerySettings?.showLastSearchQuery) return;
        setLastSearchQueries([])
        const updatedStore = [
            {
                name: StoreNameEnum.lastSearchQuery,
                fieldName: props.lastSearchQuerySettings?.fieldName,
                value: []
            }
        ]
        updateSettings({
            documentStore: updatedStore
        });
    }
    
    return (
        <>
            <DictionaryInput
                inputRef={inputRef}
                numereticInput={props.numereticInput}
                disabledInput={props.disabledInput}
                barcodeScan={props.barcodeScan}
                inputId={props.id}
                required={props.required}
                label={props.label}
                placeholder={props.placeholder ?? "-- Не выбрано --"}
                className={props.className}
                labelClassName={props.labelClassName}
                inline={props.inline}
                variant={props.variant}
                tabIndex={props.tabIndex}
                data-field-id={props["data-field-id"]}
                disabled={props.disabled}
                value={searchQuery}
                clues={props.clues}
                autoFocus={props.autoFocus}
                error={props.error}
                change={props.change ?? undefined}
                hiddenClearButton={props.hiddenClearButton}
                onChange={(value) => value && setSearchQuery(value)}
                onKeyDown={props.onKeyDown}
                onEnterClick={(value: string | undefined) => {
                    value = onClick(value)
                    selectLot(value, true)
                }}
                onMoreClick={(value) => {
                    value = onClick(value)
                    selectLot(value, false)
                }}
                onClearClick={() => {
                    deleteColumnFilter()
                    props.onChange?.("")
                    setSearchQuery("")
                    props.onClear?.()
                    handleClearLastSearchQueries()
                }}
                onFocus={showLastSearchQuery ? handleFocus : props.onFocus}
            />
            {visible && (
                <props.selectorModalJsx
                    idGlobal={props.idGlobal}
                    idTable={props?.idTable}
                    isEnabledTo={props.isEnabledTo}
                    masterIdGlobal={props.masterIdGlobal}
                    gosContract={props.gosContract}
                    gridDisplayName={props.searchOptionTitle}
                    idStoreSupplier={props.idStoreSupplier}
                    getUniqueGoodsByStore={props.getUniqueGoodsByStore}
                    onlyLotRemain={props.onlyLotRemain}
                    gridFilter={gridFilter}
                    dispatchGridFilter={props.dispatchGridFilter}
                    selectedEntity={props.selectedEntity}
                    searchQuery={searchQuery}
                    isCasheSave={props?.isCasheSave}
                    data={props?.data}
                    ok={(entity: IEntitySimpleDTO) => {
                        handleCloseModal()
                        setSearchQuery("")
                        deleteColumnFilter()
                        props.onSelect?.(props.numereticInput ? parseFloat(entity.displayName) : entity, searchQuery)
                    }}
                    cancel={(verified: boolean) => {
                        verified && props.onClear?.()
                        deleteColumnFilter()
                        handleCloseModal()
                    }}
                    setCasheData={(data) => props.setCasheData && props?.setCasheData(data)}
                    treeViewCheckDirectoryType={props.treeViewCheckDirectoryType}
                    isEnterPressed={isEnterPressed}
                    setIsEnterPressed={() => setIsEnterPressed(false)}
                    applyUserFilter={props.applyUserFilter}
                    excludeUserFilter={props.excludeUserFilter}
                    excludeContractors={props.excludeContractors}
                    activateGroupProcessing={props.activateGroupProcessing ?? false}
                    permission={props.permission}
                    documentType={props.documentType}
                />
            )}
            {lotsViewItem.item && lotsViewItem.show &&
                (lotsViewItem.multiple ?
                    (
                        <LotsMultipleSelectorModal
                            ok={(lot: IEntitySimpleDTO) => {
                                props.onSelect?.(lot, lotsViewItem.queryArg)
                            }}
                            cancel={() => setLotsViewItem({ item: lotsViewItem.item, show: false, multiple: true })}
                            data={lotsViewItem.item}
                            activateGroupProcessing={props.activateGroupProcessing}
                        />
                    ) :
                    (
                        <LotsSimpleSelectorModal
                            ok={(lot: IEntitySimpleDTO) => {
                                props.onSelect?.(lot, lotsViewItem.queryArg)
                            }}
                            cancel={() => setLotsViewItem({ item: lotsViewItem.item, show: false, multiple: true })}
                            data={lotsViewItem.item}
                            activateGroupProcessing={props.activateGroupProcessing}
                        />
                    )
                )}
        </>
    )
}

export default GridSelectorInput
